<template>
    <div>
        <b-row>
            <b-col cols="5">
                <h3>Preparation Steps</h3>
                <a href="#" @click.prevent="openForm">
                    <span>New Step</span> <i class="fas fa-plus-circle"></i>
                </a>
            </b-col>
        </b-row>
        <b-row v-if="showForm">
            <b-col cols="12">
                <b-card>
                    <b-row>
                        <b-col cols="2">
                            <fg-input v-model.number="upsertStepNumber" label="Step #" type="number" required></fg-input>
                        </b-col>
                        <b-col cols="10">
                            <fg-input v-model="upsertDescription" label="Description" type="text" required></fg-input>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="3">
                            <b-form-checkbox v-model="upsertRequireWeight" :disabled="upsertIsLinked"
                                :title="upsertIsLinked ? 'A step linked to an ingredient always requires weight' : ''">
                                Require Weight
                            </b-form-checkbox>
                        </b-col>
                        <b-col cols="3">
                            <b-form-checkbox v-model="upsertRequirePicture">Require Picture</b-form-checkbox>
                        </b-col>
                        <b-col cols="3">
                            <b-form-checkbox v-model="upsertRequirePharmacistSignOff">Require Pharmacist Sign-Off</b-form-checkbox>
                        </b-col>
                    </b-row>
                    <b-row v-if="upsertRequireWeight" class="mt-2">
                        <b-col cols="12" md="4">
                            <b-form-group label="Weigh-out of ingredient" class="mb-2"
                                description="Link the step to a formula ingredient and its expected weight is derived from that ingredient's weigh-out at fill time.">
                                <b-form-select v-model="upsertCompoundIngredientID" :options="ingredientOptions" @change="onLinkedIngredientChange" />
                            </b-form-group>
                        </b-col>
                        <template v-if="upsertIsLinked">
                            <b-col cols="6" md="2">
                                <fg-input v-model.number="upsertProportionPercent" label="Proportion (%)" type="number" step="0.01" min="0" max="100"
                                    :disabled="upsertIsRemainderStep" :required="!upsertIsRemainderStep"></fg-input>
                            </b-col>
                            <b-col cols="6" md="3" class="pt-md-4">
                                <b-form-checkbox v-model="upsertIsRemainderStep" class="mt-md-2">
                                    Remainder (QS) step
                                    <small class="text-muted d-block">Weighs out whatever the other linked steps leave.</small>
                                </b-form-checkbox>
                            </b-col>
                        </template>
                        <b-col v-else cols="6" md="3">
                            <fg-input v-model.number="upsertExpectedWeight" label="Expected Weight" type="number" step="0.01" required></fg-input>
                        </b-col>
                        <b-col cols="6" md="3">
                            <fg-input v-model.number="upsertWeightTolerance" label="Tolerance (%)" type="number" step="0.1" required></fg-input>
                        </b-col>
                    </b-row>
                    <b-row v-if="upsertIsLinked">
                        <b-col cols="12">
                            <div class="small text-muted">
                                <b-icon icon="info-circle" />
                                <template v-if="upsertIsRemainderStep">
                                    Expected weight at fill time = the ingredient's weigh-out less the other linked steps
                                </template>
                                <template v-else-if="upsertProportionPreview !== null">
                                    Expected weight at fill time = {{ upsertProportionPercent }}% of the ingredient's weigh-out
                                    (formula: {{ upsertProportionPreview }})
                                </template>
                                <template v-else>
                                    Enter the share of the ingredient's weigh-out this step weighs, or mark it as the remainder step
                                </template>
                            </div>
                            <div v-for="(line, index) in upsertLinkSummary" :key="index" class="small"
                                :class="line.ok ? 'text-success' : 'text-warning'">
                                <b-icon :icon="line.ok ? 'check-circle' : 'exclamation-triangle'" /> {{ line.text }}
                            </div>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col cols="12">
                            <b-button variant="success" @click="submitUpsert" :disabled="!canSaveStep">Save</b-button>
                            <b-button @click="onCancelClick">Cancel</b-button>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>
        </b-row>
        <b-row v-if="linkProblems.length > 0">
            <b-col>
                <b-alert show variant="warning" class="py-2 mb-2">
                    <div v-for="(problem, index) in linkProblems" :key="index" class="small">
                        <i class="fas fa-exclamation-triangle" /> {{ problem }}
                    </div>
                </b-alert>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-table :fields="stepFields" :items="sortedSteps">
                    <template #cell(requireWeight)="data">
                        <b-icon v-if="data.value" icon="check-circle-fill" variant="success"></b-icon>
                    </template>
                    <template #cell(requirePicture)="data">
                        <b-icon v-if="data.value" icon="check-circle-fill" variant="success"></b-icon>
                    </template>
                    <template #cell(expectedWeight)="data">
                        <template v-if="data.item.compoundIngredientID != null">
                            <span class="text-muted font-italic" title="Derived from the linked ingredient's weigh-out at fill time">derived</span>
                        </template>
                        <template v-else>{{ data.value }}</template>
                    </template>
                    <template #cell(linkedIngredient)="data">
                        <template v-if="data.item.compoundIngredientID != null">
                            <i class="fas fa-link text-muted" />
                            {{ ingredientNameFor(data.item.compoundIngredientID) }}
                            <span class="text-muted">· {{ describeShare(data.item) }}</span>
                            <b-badge v-if="!ingredientExists(data.item.compoundIngredientID)" variant="warning" class="ml-1"
                                title="Edit the step and choose another ingredient, or unlink it">
                                no longer in the formula
                            </b-badge>
                        </template>
                    </template>
                    <template #cell(requirePharmacistSignOff)="data">
                        <b-icon v-if="data.value" icon="check-circle-fill" variant="success"></b-icon>
                    </template>
                    <template #cell(actions)="data">
                        <div class="text-right">
                            <a href="#" @click.prevent="updateStep(data.item)">
                                <b-icon v-b-tooltip="'Edit'" icon="pencil-square"/>
                            </a>
                            <a href="#" @click.prevent="deleteConfirmDialog(data.item)">
                                <b-icon v-b-tooltip="'Remove'" class="mx-1" icon="x-circle-fill" variant="danger"/>
                            </a>
                        </div>
                    </template>
                </b-table>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
    import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
    import { CompoundIngredients, CompoundSteps } from '@/models/Drug/CompoundDrug';
    import { calculateStepExpectedWeight, describeStepLinkProblems, toNumberOrNull, validateStepLinks } from '@/util/activityFactor';

    /** One line of the live per-ingredient summary shown under the form while linking a step. */
    export interface LinkSummaryLine {
        ok: boolean;
        text: string;
    }

    @Component({
        name: "PreparationStepsComponent",
        components: {}
    })
    export default class PreparationStepsComponent extends Vue {

        @Prop() private compoundId!: number;
        @Prop() private steps!: CompoundSteps[];
        /** The formula's ingredients, so a weighed step can be linked to one; unsaved ingredients carry a temporary negative id. */
        @Prop({ default: () => [] }) private ingredients!: CompoundIngredients[];

        private showForm: boolean = false;
        private upsertId: number = 0;
        private upsertStepNumber: number = 1;
        private upsertDescription: string = "";
        private upsertRequireWeight: boolean = false;
        private upsertRequirePicture: boolean = false;
        private upsertWeightTolerance: number | null = null;
        private upsertExpectedWeight: number | null = null;
        private upsertRequirePharmacistSignOff: boolean = false;
        private upsertCompoundIngredientID: number | null = null;
        private upsertProportionPercent: number | null = null;
        private upsertIsRemainderStep: boolean = false;

        private stepFields = [
            { key: "stepNumber", label: "Step #" },
            { key: "description", label: "Description" },
            { key: "requireWeight", label: "Req. Weight" },
            { key: "requirePicture", label: "Req. Picture" },
            { key: "linkedIngredient", label: "Ingredient" },
            { key: "expectedWeight", label: "Expected Weight" },
            { key: "weightTolerance", label: "Tolerance (%)" },
            { key: "requirePharmacistSignOff", label: "RPh Sign-Off" },
            { key: "actions", label: "" },
        ]

        get sortedSteps() {
            return this.steps ? [...this.steps].sort((a, b) => a.stepNumber - b.stepNumber) : [];
        }

        get upsertIsLinked(): boolean {
            return this.upsertCompoundIngredientID != null;
        }

        get ingredientOptions() {
            const options: { value: number | null; text: string }[] = [
                { value: null, text: '— none: enter an expected weight —' },
            ];
            for (const ingredient of this.ingredients ?? []) {
                const flag = ingredient.applyActivityFactor ? ' · activity factor' : '';
                options.push({ value: ingredient.id, text: `${this.ingredientNameFor(ingredient.id)} — ${ingredient.quantity} ${ingredient.unitOfMeasure ?? ''}${flag}` });
            }
            return options;
        }

        get upsertIngredient(): CompoundIngredients | null {
            if (this.upsertCompoundIngredientID == null) return null;
            return (this.ingredients ?? []).find(ingredient => ingredient.id === this.upsertCompoundIngredientID) ?? null;
        }

        /** "0.5 g of 1 g" — the share applied to the formula quantity, as a sanity check while typing. */
        get upsertProportionPreview(): string | null {
            const proportion = toNumberOrNull(this.upsertProportionPercent);
            const ingredient = this.upsertIngredient;
            if (proportion === null || proportion <= 0 || !ingredient) return null;
            const share = calculateStepExpectedWeight(Number(ingredient.quantity) || 0, proportion);
            const unit = ingredient.unitOfMeasure ?? '';
            return `${Number(share.toFixed(4))} ${unit} of ${ingredient.quantity} ${unit}`.trim();
        }

        /**
         * The linked steps of the ingredient being edited, with this form's values in place of the
         * step being edited, validated as one set — so the user sees whether the shares add up before
         * saving the compound.
         */
        get upsertLinkSummary(): LinkSummaryLine[] {
            if (this.upsertCompoundIngredientID == null) return [];
            const others = (this.steps ?? []).filter(step => step.id !== this.upsertId && step.compoundIngredientID === this.upsertCompoundIngredientID);
            const set = [
                ...others.map(step => ({ stepNumber: step.stepNumber, proportionPercent: step.proportionPercent, isRemainder: !!step.isRemainderStep })),
                { stepNumber: this.upsertStepNumber, proportionPercent: this.upsertProportionPercent, isRemainder: this.upsertIsRemainderStep },
            ].sort((a, b) => a.stepNumber - b.stepNumber);

            const parts = set.map(step => `${step.stepNumber} (${step.isRemainder ? 'remainder' : `${toNumberOrNull(step.proportionPercent) ?? 0}%`})`);
            const label = `${this.ingredientNameFor(this.upsertCompoundIngredientID)}: step${set.length === 1 ? '' : 's'} ${parts.join(', ')}`;
            const validation = validateStepLinks(set);
            if (validation.isValid) return [{ ok: true, text: `${label} — complete weigh-out` }];
            return validation.errors.map(error => ({ ok: false, text: `${label} — ${error}` }));
        }

        /** Problems across the whole step list (the host page refuses to save while any remain). */
        get linkProblems(): string[] {
            const problems = describeStepLinkProblems(this.steps ?? [], id => this.ingredientNameFor(id));
            for (const step of this.sortedSteps) {
                if (step.compoundIngredientID != null && !this.ingredientExists(step.compoundIngredientID)) {
                    problems.push(`Step ${step.stepNumber} is linked to an ingredient that is no longer in the formula — edit the step and choose another ingredient, or unlink it.`);
                }
            }
            return problems;
        }

        get canSaveStep(): boolean {
            // Step number and description are always required
            if (!this.upsertStepNumber || !this.upsertDescription) {
                return false;
            }

            // If weight is required, a tolerance is needed plus a target: an expected weight, or a link with
            // a positive share (or the remainder flag)
            if (this.upsertRequireWeight) {
                if (this.upsertWeightTolerance == null) return false;
                if (this.upsertIsLinked) {
                    if (!this.upsertIsRemainderStep && !((toNumberOrNull(this.upsertProportionPercent) ?? 0) > 0)) return false;
                } else if (this.upsertExpectedWeight == null) {
                    return false;
                }
            }

            return true;
        }

        ingredientExists(compoundIngredientID: number): boolean {
            return (this.ingredients ?? []).some(ingredient => ingredient.id === compoundIngredientID);
        }

        ingredientNameFor(compoundIngredientID: number): string {
            const ingredient = (this.ingredients ?? []).find(i => i.id === compoundIngredientID);
            return ingredient?.drugName || `Ingredient ${compoundIngredientID}`;
        }

        describeShare(step: CompoundSteps): string {
            return step.isRemainderStep ? 'remainder' : `${step.proportionPercent ?? 0}%`;
        }

        onLinkedIngredientChange() {
            if (this.upsertIsLinked) {
                // A linked step is a weigh-out of that ingredient: it always requires weight, and its
                // target is derived rather than typed.
                this.upsertRequireWeight = true;
                this.upsertExpectedWeight = null;
            } else {
                this.upsertProportionPercent = null;
                this.upsertIsRemainderStep = false;
            }
        }

        submitUpsert() {
            // Validate before submitting
            if (!this.canSaveStep) {
                this.$notification.error('Please fill in all required fields. When weight is required, provide the tolerance and either an expected weight or a linked ingredient with its share.');
                return;
            }
            const step = new CompoundSteps();
            step.id = this.upsertId || 0;
            step.compoundID = this.compoundId;
            step.stepNumber = this.upsertStepNumber;
            step.description = this.upsertDescription;
            step.requireWeight = this.upsertRequireWeight;
            step.requirePicture = this.upsertRequirePicture;
            step.weightTolerance = this.upsertWeightTolerance;
            step.requirePharmacistSignOff = this.upsertRequirePharmacistSignOff;
            const linked = this.upsertRequireWeight && this.upsertIsLinked;
            step.compoundIngredientID = linked ? this.upsertCompoundIngredientID : null;
            step.isRemainderStep = linked && this.upsertIsRemainderStep;
            step.proportionPercent = linked && !this.upsertIsRemainderStep ? toNumberOrNull(this.upsertProportionPercent) : null;
            step.expectedWeight = linked ? null : this.upsertExpectedWeight;

            const existingIndex = this.steps.findIndex(item => item.id === step.id);
            if (existingIndex > -1) {
                this.steps.splice(existingIndex, 1);
            }
            this.steps.push(step);
            this.closeForm();
            this.$emit('continue');
        }

        updateStep(item: CompoundSteps) {
            this.showForm = true;
            this.upsertId = item.id;
            this.upsertStepNumber = item.stepNumber;
            this.upsertDescription = item.description;
            this.upsertRequireWeight = item.requireWeight;
            this.upsertRequirePicture = item.requirePicture;
            this.upsertWeightTolerance = item.weightTolerance;
            this.upsertExpectedWeight = item.expectedWeight;
            this.upsertRequirePharmacistSignOff = item.requirePharmacistSignOff || false;
            this.upsertCompoundIngredientID = item.compoundIngredientID ?? null;
            this.upsertProportionPercent = item.proportionPercent ?? null;
            this.upsertIsRemainderStep = !!item.isRemainderStep;
        }

        deleteConfirmDialog(item: CompoundSteps) {
            this.$bvModal.msgBoxConfirm(`This preparation step will be deleted. Are you sure?`, {
                title: 'Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'YES',
                cancelTitle: 'NO',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            })
                .then(value => {
                    if (!value) return;
                    this.deleteStep(item);
                })
                .catch(err => {
                    console.error("Error caught on Delete button.", err);
                });
        }

        deleteStep(item: CompoundSteps) {
            if (!item) return;
            const itemIndex = this.steps.findIndex(s => s.id === item.id);
            if (itemIndex < 0) return;
            this.steps.splice(itemIndex, 1);
            this.$emit('delete');
        }

        openForm() {
            this.clearForm();
            this.showForm = true;
        }

        closeForm() {
            this.clearForm();
            this.showForm = false;
        }

        clearForm() {
            this.upsertId = Math.min(...this.steps.map(item => item.id), 0) - 1;
            if (this.upsertId > 0) this.upsertId = 0;
            this.upsertStepNumber = this.steps.length > 0 ? Math.max(...this.steps.map(s => s.stepNumber)) + 1 : 1;
            this.upsertDescription = "";
            this.upsertRequireWeight = false;
            this.upsertRequirePicture = false;
            this.upsertWeightTolerance = null;
            this.upsertExpectedWeight = null;
            this.upsertRequirePharmacistSignOff = false;
            this.upsertCompoundIngredientID = null;
            this.upsertProportionPercent = null;
            this.upsertIsRemainderStep = false;
        }

        @Emit('cancel')
        onCancelClick() {
            this.closeForm();
        }
    }
</script>
